// ─── analyzeMealPhotoDay ─────────────────────────────────────────────────
// 2026-07-21 — Lecture IA des photos de repas. Né en mode démo (décision
// Anissa), ÉTENDU aux vraies clientes le 2026-07-22 après son feu vert :
// opt-in cliente par cliente (meal_photo_ai_enabled, réglages app de la
// fiche), jamais profils TCA (verrou registre + fiches jumelles dans
// SuiviCockpit.isPhotoAiAllowed), clinique-guard GO conditionnel appliqué.
//
// Contrat produit (design validé Benoit 2026-07-21) :
// - "lecture"  : privée, factuelle, pour Anissa seulement — décrit ce qui est
//   visible, compare au plan en cours, honnête sur ce qu'une photo ne peut
//   pas montrer. Jamais affichée à la cliente.
// - "brouillon": message doux dans la voix d'Anissa, TOUJOURS bienveillant.
//   S'il y a un écart à discuter, c'est Anissa qui l'écrit elle-même. Le
//   brouillon remplit la zone de réponse, elle relit, modifie, envoie.
// - Fail-closed : JSON invalide ou champ manquant → throw, rien ne s'affiche.
//
// Technique : les photos partent en blocs image par URL signée (bucket privé,
// URLs valides 1 h, générées par feedbacks-stream). callClaude passe le
// tableau de blocs tel quel au proxy /api/claude ; la garde de vocabulaire
// compliance est injectée d'office dans le system.

import { callClaude } from "./anthropic";

const SYSTEM = `Tu assistes Anissa Deroubaix, nutritionniste, dans son espace de suivi. Elle t'envoie les photos de repas d'une journée d'une cliente, ainsi que le plan alimentaire en cours de cette cliente. Tu produis DEUX textes, en JSON strict.

1. "lecture" — une lecture PRIVÉE pour Anissa (jamais montrée à la cliente) :
- Factuelle et sobre : décris ce qui est visible sur chaque photo.
- Compare au plan : correspondances et écarts FACTUELS (« le plan prévoit X au déjeuner, la photo montre plutôt Y »).
- Honnête sur l'incertitude : une photo ne montre ni les quantités exactes, ni les cuissons, ni les assaisonnements, ni ce qui a été mangé hors cadre. Ne devine pas ; dis explicitement ce que tu ne peux pas savoir.
- INTERDIT : tout diagnostic, tout jugement moral, toute extrapolation sur la santé, tout chiffre inventé (calories, grammes).
- 3 à 6 phrases.

2. "brouillon" — un brouillon de message à la cliente, dans la voix d'Anissa (tutoiement, chaleureuse mais directe, oral naturel, zéro emoji). Elle relira et modifiera avant tout envoi :
- TOUJOURS bienveillant : jamais de reproche, jamais de mise en garde, jamais de commentaire négatif sur un aliment photographié.
- Aucun compliment ni commentaire valorisant sur un aliment ou une assiette en particulier (pas de « équilibré », « bon choix », « sain »). La chaleur porte sur la démarche (merci d'avoir partagé tes photos), jamais sur le contenu de l'assiette.
- Si un écart au plan mérite discussion, ne le formule PAS : reste sur l'accusé de réception chaleureux et, au besoin, « on regarde ça ensemble à la prochaine consultation ». C'est Anissa qui décide d'écrire le reste.
- Aucun chiffre, aucun conseil nutritionnel nouveau, aucun complément, aucun dosage, aucune interprétation de symptôme.
- 2 à 4 phrases. Pas de signature.

Réponds UNIQUEMENT avec {"lecture": "...", "brouillon": "..."} — aucun autre texte.`;

/**
 * @param {object} params
 * @param {string} params.clientFirstName
 * @param {Array<{url: string, slotLabel?: string, note?: string}>} params.photos
 *   photos du jour (URLs signées) — 8 max, l'app cliente plafonne déjà à 8/jour
 * @param {string} params.planText - plan alimentaire en cours (nutritionPlan)
 * @returns {Promise<{lecture: string, brouillon: string}>}
 */
export async function analyzeMealPhotoDay({ clientFirstName, photos, planText }) {
  const usable = (photos || []).filter((p) => p && p.url).slice(0, 8);
  if (usable.length === 0) throw new Error("Aucune photo exploitable");

  const content = [];
  usable.forEach((p, i) => {
    const note = p.note ? ` — note de la cliente : « ${String(p.note).slice(0, 300)} »` : "";
    content.push({ type: "text", text: `Photo ${i + 1} — ${p.slotLabel || "repas"}${note}` });
    content.push({ type: "image", source: { type: "url", url: p.url } });
  });
  content.push({
    type: "text",
    text: [
      `Cliente : ${clientFirstName || "la cliente"}.`,
      "",
      "Plan alimentaire en cours (extrait) :",
      String(planText || "").trim()
        ? String(planText).slice(0, 6000)
        : "(Aucun plan retrouvé — dans la lecture, signale-le et décris seulement les photos, sans comparaison.)",
      "",
      "Produis le JSON demandé.",
    ].join("\n"),
  });

  const obj = await callClaude({
    system: SYSTEM,
    user: content,
    model: "claude-sonnet-5",
    maxTokens: 900,
    parseJson: true,
  });

  const lecture = String(obj?.lecture || "").trim();
  const brouillon = String(obj?.brouillon || "").trim();
  if (!lecture || !brouillon) throw new Error("Analyse incomplète — réessaie");
  return { lecture: lecture.slice(0, 1600), brouillon: brouillon.slice(0, 900) };
}
